import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ConnectionFilesTable, {
  useConnectionIdsAndFiltersState,
} from 'instruments/connection-files/ConnectionFilesTable';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {FileListingGraphQL} from '../../../../graphql-fragments/files/__generated/FileListingGraphQL';
import {ConnectionDataQuery, ConnectionDataQueryVariables} from './__generated/ConnectionDataQuery';

export interface ConnectionDataProps {
  connectionId: string;
  dataCatalogFolderId: string | null;
  filesToSync?: number;
}

function ConnectionData(props: ConnectionDataProps): JSX.Element {
  const [files, setFiles] = useState<Array<FileListingGraphQL>>([]);
  const initialConnectionIds = useMemo(() => [props.connectionId], [props.connectionId]);

  const {
    filtersState: [filters, setFilters],
  } = useConnectionIdsAndFiltersState({initialConnectionIds, initialFilters: null});

  return (
    <div className="react-tab-container">
      {props.filesToSync != null && props.filesToSync > 0 ? (
        <div className={css(styles.filesToSync)}>
          <BenchlingIcon className={css(styles.filesToSyncIcon)} icon="request-in-progress" />
          {props.filesToSync} file{props.filesToSync !== 1 ? 's' : ''} remaining to sync
        </div>
      ) : null}
      <ConnectionFilesTable
        connectionFiles={files}
        setConnectionFiles={setFiles}
        columns={['File name', 'Status', 'Modified at', 'Created at', 'File path']}
        filters={filters}
        setFilters={setFilters}
        hideCheckboxColumn={true}
        onSelectRows={(rowIndices) => {
          // The checkboxes are hidden, so it shouldn't be possible to select more than one row at once
          assert(rowIndices.size === 1, 'Expected exactly one row to be selected');
          const [rowIndex] = [...rowIndices];
          const selectedFile = files[rowIndex];
          LoadingScreen.showFileLoading();
          FileBrowserActions.openFile(selectedFile.id);
        }}
      />
    </div>
  );
}

export const CONNECTION_DATA_QUERY: DocumentNode = gql`
  query ConnectionDataQuery($connectionId: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    connection(id: $connectionId) {
      ... on ConnectionNode {
        id
        dataCatalogFolder {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingConnectionData(props: {connectionId: string}): JSX.Element {
  const result = useBenchlingQuery<ConnectionDataQuery, ConnectionDataQueryVariables>(CONNECTION_DATA_QUERY, {
    variables: {connectionId: props.connectionId},
  });
  // BNCH-155408: filesToSync fetch removed until count accuracy is verified
  let children: JSX.Element;
  if (result.type !== QueryResultType.SUCCESS) {
    children = <LoadingFileScreen />;
  } else if (isInaccessible(result.data.connection)) {
    children = (
      <WorkspaceItemInaccessibleGuard inaccessibleInfo={result.data.connection} modelType="connection" />
    );
  } else {
    children = (
      <ConnectionData
        connectionId={props.connectionId}
        dataCatalogFolderId={result.data.connection.dataCatalogFolder?.id ?? null}
      />
    );
  }
  return <div className="react-tab-container">{children}</div>;
}

const styles = StyleSheet.create({
  filesToSync: {
    marginTop: 4,
    marginBottom: 8,
    color: Colors.BLUE,
    fontSize: 13,
  },
  filesToSyncIcon: {
    color: Colors.BLUE,
    marginRight: 6,
  },
});
